import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Filter,
  MapPin,
  Building2,
  Eye,
  X,
  Loader,
  AlertCircle,
  Home,
  Calendar,
  Euro,
  Users,
  ArrowLeft
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'

// Google Maps types
declare global {
  interface Window {
    google: typeof google
  }
}

// Types
interface Project {
  project_id: number
  name: string
  address: string
  city: string
  district: string
  latitude: string | number
  longitude: string | number
  status: string
  total_units: number
  available_units: number
  sold_units: number
  reserved_units?: number
  avg_sqm_price: number
  min_sqm_price?: number
  max_sqm_price?: number
  has_parking: boolean
  has_pool: boolean
  has_gym: boolean
  website: string
  listing_date?: string
  created_at?: string
  updated_at?: string
  sold_out?: boolean
  architect?: {
    name: string
  }
  developer?: {
    name: string
  }
  broker?: {
    name: string
  }
}

interface ProjectFilters {
  search: string
  status: string
  city: string
  district: string
  typology: string
  avg_price_min: number
  avg_price_max: number
  launch_date_from: string
  launch_date_to: string
}

// Filter options
const STATUS_FILTERS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'sold_out', label: 'Sold Out' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' }
]

const TYPOLOGY_FILTERS = [
  { value: '', label: 'All Types' },
  { value: 'Apartment', label: 'Apartment' },
  { value: 'House', label: 'House' },
  { value: 'Apartment & House', label: 'Apartment & House' },
  { value: 'Tourist Apartment', label: 'Tourist Apartment' }
]

const CITIES = [
  { value: '', label: 'All Cities' },
  { value: 'Lisboa', label: 'Lisboa' },
  { value: 'Porto', label: 'Porto' },
  { value: 'Cascais', label: 'Cascais' },
  { value: 'Sintra', label: 'Sintra' },
  { value: 'Vila Nova de Gaia', label: 'Vila Nova de Gaia' }
]

// Google Maps component for multiple projects
function GoogleMapsViewer({
  projects,
  onProjectSelect,
  height = '100%',
  className = ''
}: {
  projects: Project[]
  onProjectSelect: (project: Project) => void
  height?: string
  className?: string
}) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<google.maps.Marker[]>([])
  const onProjectSelectRef = useRef(onProjectSelect)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [apiKey, setApiKey] = useState<string | null>(null)

  // Update the ref when onProjectSelect changes
  useEffect(() => {
    onProjectSelectRef.current = onProjectSelect
  }, [onProjectSelect])

  // Fetch API key from backend
  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const response = await api.get('/geocoding/api-key')
        if (response.data.success && response.data.apiKey) {
          setApiKey(response.data.apiKey)
        } else {
          setError('Google Maps API key not available')
        }
      } catch (err) {
        console.error('Error fetching API key:', err)
        setError('Failed to load Google Maps API key')
      }
    }

    fetchApiKey()
  }, [])

  // Get color based on project availability
  const getStatusColor = (project: Project): string => {
    const total = Number(project.total_units || 0);
    const sold = Number(project.sold_units || 0);
    const reserved = Number(project.reserved_units || 0);
    
    if (total > (sold + reserved)) {
      return '#10b981'; // Green: Stock available (or potential stock)
    } else if (reserved > 0) {
      return '#f59e0b'; // Orange: No free stock, but some reserved
    } else {
      return '#ef4444'; // Red: Fully sold out
    }
  };

  // Initialize Google Maps
  useEffect(() => {
    if (!apiKey || projects.length === 0) return

    const initializeMap = async () => {
      if (!mapRef.current) {
        console.error('Map ref not available')
        return
      }

      // Ensure map container has dimensions
      const rect = mapRef.current.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) {
        console.error('Map container has no dimensions:', rect)
        setError('Map container has no dimensions')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        // Check if Google Maps is loaded
        if (!window.google || !window.google.maps || !window.google.maps.Map) {
          throw new Error('Google Maps API not fully loaded')
        }

        // Clear existing markers
        markersRef.current.forEach(marker => marker.setMap(null))
        markersRef.current = []

        // Calculate bounds
        const bounds = new google.maps.LatLngBounds()
        const validProjects = projects.filter(p => p.latitude && p.longitude)
        
        if (validProjects.length === 0) {
          setError('No projects with valid coordinates')
          setIsLoading(false)
          return
        }

        // Create map
        const map = new google.maps.Map(mapRef.current, {
          zoom: 10,
          center: { lat: 38.7223, lng: -9.1393 }, // Lisbon coordinates
          mapTypeId: google.maps.MapTypeId?.ROADMAP || 'roadmap',
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        })

        mapInstanceRef.current = map

        // Add markers for each project
        validProjects.forEach((project) => {
          const lat = typeof project.latitude === 'string' ? parseFloat(project.latitude) : project.latitude
          const lng = typeof project.longitude === 'string' ? parseFloat(project.longitude) : project.longitude

          if (isNaN(lat) || isNaN(lng)) {
            return
          }

          const position = { lat, lng }
          bounds.extend(position)

          // Create marker element
          const markerElement = document.createElement('div')
          markerElement.innerHTML = `
            <div style="
              width: 32px;
              height: 32px;
              background-color: ${getStatusColor(project)};
              border: 3px solid white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              cursor: pointer;
            ">
              <div style="
                width: 12px;
                height: 12px;
                background-color: white;
                border-radius: 50%;
              "></div>
            </div>
          `

          // Create marker
          const marker = new google.maps.Marker({
            position,
            map,
            title: project.name,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="12" fill="${getStatusColor(project)}" stroke="white" stroke-width="3"/>
                  <circle cx="16" cy="16" r="6" fill="white"/>
                </svg>
              `)}`,
              scaledSize: new google.maps.Size(32, 32),
              anchor: new google.maps.Point(16, 16)
            }
          })

          // Add click handler
          marker.addListener('click', () => {
            onProjectSelectRef.current(project)
          })

          // Add info window
          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div style="padding: 12px; min-width: 200px;">
                <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600; color: #1f2937;">
                  ${project.name || 'Unnamed Project'}
                </h3>
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #6b7280;">
                  ${project.address || 'No address'}
                </p>
                <p style="margin: 0 0 8px 0; font-size: 12px; color: #6b7280;">
                  ${project.city || 'Unknown City'}, ${project.district || 'Unknown District'}
                </p>
                <div style="margin-top: 8px;">
                  <span style="
                    display: inline-block;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 10px;
                    font-weight: 500;
                    background-color: ${getStatusColor(project)};
                    color: white;
                  ">
                    ${(() => {
                      const total = Number(project.total_units || 0);
                      const sold = Number(project.sold_units || 0);
                      const reserved = Number(project.reserved_units || 0);
                      if (total > (sold + reserved)) return 'STOCK AVAILABLE';
                      if (reserved > 0) return 'RESERVED ONLY';
                      return 'FULLY SOLD OUT';
                    })()}
                  </span>
                </div>
              </div>
            `
          })

          // Open info window on click
          marker.addListener('click', () => {
            infoWindow.open(map, marker)
          })

          markersRef.current.push(marker)
        })

        // Fit bounds to show all markers
        if (validProjects.length > 1) {
          map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 })
        } else if (validProjects.length === 1) {
          map.setCenter(bounds.getCenter())
          map.setZoom(15)
        }

        setIsLoading(false)
      } catch (err) {
        console.error('Error initializing Google Maps:', err)
        setError('Failed to load Google Maps')
        setIsLoading(false)
        toast.error('Failed to load map')
      }
    }

    // Load Google Maps script if not already loaded
    if (!window.google || !window.google.maps) {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`
      script.async = true
      script.defer = true
      script.onload = () => {
        setTimeout(initializeMap, 500)
      }
      script.onerror = () => {
        setError('Failed to load Google Maps API')
        setIsLoading(false)
        toast.error('Failed to load Google Maps API')
      }
      document.head.appendChild(script)
    } else {
      setTimeout(initializeMap, 500)
    }

    // Cleanup
    return () => {
      markersRef.current.forEach(marker => marker.setMap(null))
      markersRef.current = []
    }
  }, [apiKey, projects])

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-6 text-center ${className}`}>
        <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
        <h3 className="text-sm font-medium text-red-800 mb-1">Map Error</h3>
        <p className="text-xs text-red-600">{error}</p>
      </div>
    )
  }

  return (
    <div className={`relative ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      )}
      <div
        ref={mapRef}
        style={{ height: height || '600px', minHeight: '400px' }}
        className="w-full rounded-lg border border-gray-200"
      />

      {/* Map Legend */}
      <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm p-3 rounded-xl shadow-tagus border border-secondary-200 z-10 max-w-[200px]">
        <h4 className="text-[10px] font-bold text-secondary-500 uppercase tracking-widest mb-2 px-1">Availability</h4>
        <div className="space-y-2">
          <div className="flex items-center space-x-2 px-1">
            <div className="w-3 h-3 rounded-full bg-[#10b981] border border-white shadow-sm"></div>
            <span className="text-xs font-semibold text-secondary-700">Stock Available</span>
          </div>
          <div className="flex items-center space-x-2 px-1">
            <div className="w-3 h-3 rounded-full bg-[#f59e0b] border border-white shadow-sm"></div>
            <span className="text-xs font-semibold text-secondary-700">Reserved Only</span>
          </div>
          <div className="flex items-center space-x-2 px-1">
            <div className="w-3 h-3 rounded-full bg-[#ef4444] border border-white shadow-sm"></div>
            <span className="text-xs font-semibold text-secondary-700">Fully Sold Out</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Project detail modal component
function ProjectDetailModal({ 
  project, 
  isOpen, 
  onClose,
  navigate
}: { 
  project: Project | null
  isOpen: boolean
  onClose: () => void
  navigate: (path: string) => void
}) {
  if (!isOpen || !project) return null

  const conversionRateValue = project.total_units > 0 
    ? Math.round(((Number(project.sold_units) + Number(project.reserved_units || 0)) / Number(project.total_units)) * 100) 
    : 0

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[95vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 truncate pr-4">{project.name}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6 max-h-[calc(95vh-140px)] overflow-y-auto">
          <div className="space-y-4 sm:space-y-6">
            {/* Project Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">Project Information</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Address</label>
                  <p className="text-gray-900 text-sm">{project.address || 'No address available'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Location</label>
                  <p className="text-gray-900 text-sm">{project.city && project.district ? `${project.city}, ${project.district}` : 'Location not available'}</p>
              </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${(() => {
                    const total = Number(project.total_units || 0);
                    const sold = Number(project.sold_units || 0);
                    const reserved = Number(project.reserved_units || 0);
                    if (total > (sold + reserved)) return 'bg-success-100 text-success-800';
                    if (reserved > 0) return 'bg-warning-100 text-warning-800';
                    return 'bg-danger-100 text-danger-800';
                  })()}`}>
                    {(() => {
                      const total = Number(project.total_units || 0);
                      const sold = Number(project.sold_units || 0);
                      const reserved = Number(project.reserved_units || 0);
                      if (total > (sold + reserved)) return 'Stock Available';
                      if (reserved > 0) return 'Reserved Only';
                      return 'Sold Out';
                    })()}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Website</label>
                  {project.website ? (
                    <a href={project.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 text-sm">
                      Visit Website
                    </a>
                  ) : (
                    <p className="text-gray-500 text-sm">No website available</p>
                  )}
              </div>
            </div>
          </div>

                     {/* Project Stats */}
                     <div>
                       <h4 className="text-md font-semibold text-gray-900 mb-3">Project Statistics</h4>
                       <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                         <div className="text-center p-3 bg-gray-50 rounded-lg">
                           <div className="text-xl sm:text-2xl font-bold text-gray-900">{project.total_units}</div>
                           <div className="text-xs text-gray-600">Total Units</div>
                         </div>
                         <div className="text-center p-3 bg-gray-50 rounded-lg">
                           <div className="text-xl sm:text-2xl font-bold text-gray-900">{project.sold_units}</div>
                           <div className="text-xs text-gray-600">Sold Units</div>
                         </div>
                         <div className="text-center p-3 bg-gray-50 rounded-lg">
                           <div className="text-xl sm:text-2xl font-bold text-gray-900">{project.reserved_units || 0}</div>
                           <div className="text-xs text-gray-600">Reserved</div>
                         </div>
                         <div className="text-center p-3 bg-blue-50 rounded-lg">
                           <div className="text-xl sm:text-2xl font-bold text-blue-900">{conversionRateValue}%</div>
                           <div className="text-xs text-blue-600">Conversion Rate</div>
                         </div>
                         <div className="text-center p-3 bg-gray-50 rounded-lg">
                           <div className="text-xl sm:text-2xl font-bold text-gray-900">€{Math.round(project.avg_sqm_price || 0).toLocaleString()}</div>
                           <div className="text-xs text-gray-600">Avg Price/m²</div>
                         </div>
                       </div>
                     </div>
              </div>
            </div>

        <div className="flex flex-col sm:flex-row items-center justify-end p-4 sm:p-6 border-t border-gray-200 space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Close
          </button>
            <button
            onClick={() => {
              navigate(`/projects/${project.project_id}`)
              onClose()
            }}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center"
          >
            <Eye className="h-4 w-4 mr-2" />
              View Details
            </button>
        </div>
      </div>
    </div>
  )
}

export function ProjectsMap() {
  const navigate = useNavigate()
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  // Simplified filters
  const [filters, setFilters] = useState<ProjectFilters>({
    search: '',
    status: '',
    city: '',
    district: '',
    typology: '',
    avg_price_min: 0,
    avg_price_max: 15000,
    launch_date_from: '',
    launch_date_to: ''
  })

  // Fetch projects with coordinates
  const { data: projectsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['projects-map', filters],
    queryFn: async () => {
        const params = new URLSearchParams()
        
      // Always request projects with coordinates for the map
      params.append('has_coordinates', 'true')
      
      // Set a high limit to get all projects (backend default is 20, we have 1545+ projects)
      params.append('limit', '2000')
      
      // Add all filters
        if (filters.search) params.append('search', filters.search)
      if (filters.status) params.append('status', filters.status)
        if (filters.city) params.append('city', filters.city)
        if (filters.district) params.append('district', filters.district)
      if (filters.typology) params.append('typology', filters.typology)
      if (filters.avg_price_min > 0) params.append('min_sqm_price', filters.avg_price_min.toString())
      if (filters.avg_price_max < 15000) params.append('max_sqm_price', filters.avg_price_max.toString())
      
      // Add launch date filters
      if (filters.launch_date_from) params.append('listing_date_from', filters.launch_date_from)
      if (filters.launch_date_to) params.append('listing_date_to', filters.launch_date_to)

      const response = await api.get(`/projects?${params.toString()}`)
        return response.data
      }
  })

  const projects = projectsResponse?.data || []
  const totalProjects = projectsResponse?.meta?.total || 0

  // Filter projects with valid coordinates
  const projectsWithCoordinates = useMemo(() => {
    return projects.filter((project: Project) => {
      const lat = typeof project.latitude === 'string' ? parseFloat(project.latitude) : project.latitude
      const lng = typeof project.longitude === 'string' ? parseFloat(project.longitude) : project.longitude
      return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0
    })
  }, [projects])

  const handleProjectSelect = useCallback((project: Project) => {
    setSelectedProject(project)
  }, [])

  const handleFilterChange = (key: keyof ProjectFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      city: '',
      district: '',
      typology: '',
      avg_price_min: 0,
      avg_price_max: 15000,
      launch_date_from: '',
      launch_date_to: ''
    })
  }

  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'avg_price_min' || key === 'avg_price_max') {
      return false // Don't count price range as active filters
    }
    return value !== '' && value !== false && value !== 0
  }).length

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 text-danger-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-secondary-900 mb-2">Error loading projects</h3>
        <p className="text-secondary-500 mb-4">There was an error loading the projects data.</p>
        <button onClick={() => refetch()} className="btn-primary">
          Try Again
          </button>
      </div>
    )
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Compact Header */}
      <div className="bg-white shadow-sm border-b border-secondary-200 px-4 py-2 flex-shrink-0 z-20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/projects')}
              className="btn-secondary py-1.5 px-3 flex items-center h-9"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back
            </button>
            <div className="h-6 w-px bg-secondary-200 hidden sm:block"></div>
            <div>
              <h1 className="text-base font-bold text-secondary-900 leading-tight">Projects Map</h1>
              <p className="text-xs text-secondary-500">
                {isLoading ? 'Loading...' : `${projectsWithCoordinates.length} locations`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-secondary py-1.5 h-9 flex items-center ${
                activeFiltersCount > 0 
                  ? 'border-primary-200 bg-primary-50 text-primary-700' 
                  : ''
              }`}
            >
              <Filter className="h-3.5 w-3.5 mr-2" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold text-white bg-primary-600 rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Compact Filters Panel */}
      {showFilters && (
        <div className="bg-white border-b border-secondary-200 px-4 py-3 flex-shrink-0 z-10 transition-all duration-300 ease-in-out">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-secondary-900 uppercase tracking-wide">Advanced Filters</h3>
            <button
              onClick={() => setShowFilters(false)}
              className="text-secondary-400 hover:text-secondary-600 transition-colors p-1"
            >
              <X className="h-5 w-5" />
            </button>
        </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-h-96 overflow-y-auto">
            {/* Search */}
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="text-xs font-semibold text-secondary-700 mb-1 block">Search Projects</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search by name, city, or district..."
                  className="form-input pl-10 h-9"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="text-xs font-semibold text-secondary-700 mb-1 block">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="form-input h-9 py-0"
              >
                {STATUS_FILTERS.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </select>
            </div>

            {/* City Filter */}
            <div>
              <label className="text-xs font-semibold text-secondary-700 mb-1 block">City</label>
              <select
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                className="form-input h-9 py-0"
              >
                {CITIES.map((city) => (
                  <option key={city.value} value={city.value}>
                    {city.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Typology Filter */}
            <div>
              <label className="text-xs font-semibold text-secondary-700 mb-1 block">Property Type</label>
              <select
                value={filters.typology}
                onChange={(e) => handleFilterChange('typology', e.target.value)}
                className="form-input h-9 py-0"
              >
                {TYPOLOGY_FILTERS.map((typology) => (
                  <option key={typology.value} value={typology.value}>
                    {typology.label}
                  </option>
                ))}
              </select>
            </div>

                {/* AVG Price/m² Range Slider */}
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-secondary-700 mb-2 block">AVG Price/m² (€)</label>
                  <div className="space-y-3">
                    {/* Range Display */}
                    <div className="flex justify-between text-xs font-medium text-secondary-600">
                      <span>€{filters.avg_price_min.toLocaleString()}</span>
                      <span>€{filters.avg_price_max.toLocaleString()}</span>
                    </div>
                    
                    {/* Range Slider */}
                    <div className="relative">
                      <div className="relative h-6 flex items-center px-2">
                        {/* Track */}
                        <div className="absolute w-full h-1.5 bg-secondary-200 rounded-lg"></div>
                        
                        {/* Active range */}
                        <div 
                          className="absolute h-1.5 bg-primary-500 rounded-lg active-range"
                          style={{
                            left: `${(filters.avg_price_min / 15000) * 100}%`,
                            width: `${((filters.avg_price_max - filters.avg_price_min) / 15000) * 100}%`
                          }}
                        ></div>
                        
                        {/* Min thumb */}
                        <div 
                          className="absolute w-4 h-4 bg-primary-500 rounded-full border-2 border-white shadow-md cursor-pointer z-30 transform -translate-x-1/2 hover:scale-110 transition-transform"
                          style={{
                            left: `${(filters.avg_price_min / 15000) * 100}%`
                          }}
                          onMouseDown={(e) => {
                            const startX = e.clientX
                            const startValue = filters.avg_price_min
                            const trackWidth = e.currentTarget.parentElement?.offsetWidth || 0
                            let tempValue = startValue
                            
                            const handleMouseMove = (moveEvent: MouseEvent) => {
                              const deltaX = moveEvent.clientX - startX
                              const deltaPercent = (deltaX / trackWidth) * 100
                              const deltaValue = (deltaPercent / 100) * 15000
                              const newValue = Math.max(0, Math.min(15000, startValue + deltaValue))
                              
                              if (newValue <= filters.avg_price_max) {
                                tempValue = Math.round(newValue / 100) * 100
                                // Update visual position immediately without API call
                                e.currentTarget.style.left = `${(tempValue / 15000) * 100}%`
                                // Update the active range visual
                                const activeRange = e.currentTarget.parentElement?.querySelector('.active-range') as HTMLElement
                                if (activeRange) {
                                  activeRange.style.left = `${(tempValue / 15000) * 100}%`
                                  activeRange.style.width = `${((filters.avg_price_max - tempValue) / 15000) * 100}%`
                                }
                              }
                            }
                            
                            const handleMouseUp = () => {
                              // Only call API when mouse is released
                              if (tempValue !== filters.avg_price_min) {
                                handleFilterChange('avg_price_min', tempValue)
                              }
                              document.removeEventListener('mousemove', handleMouseMove)
                              document.removeEventListener('mouseup', handleMouseUp)
                            }
                            
                            document.addEventListener('mousemove', handleMouseMove)
                            document.addEventListener('mouseup', handleMouseUp)
                          }}
                        ></div>
                        
                        {/* Max thumb */}
                        <div 
                          className="absolute w-4 h-4 bg-primary-500 rounded-full border-2 border-white shadow-md cursor-pointer z-30 transform -translate-x-1/2 hover:scale-110 transition-transform"
                          style={{
                            left: `${(filters.avg_price_max / 15000) * 100}%`
                          }}
                          onMouseDown={(e) => {
                            const startX = e.clientX
                            const startValue = filters.avg_price_max
                            const trackWidth = e.currentTarget.parentElement?.offsetWidth || 0
                            let tempValue = startValue
                            
                            const handleMouseMove = (moveEvent: MouseEvent) => {
                              const deltaX = moveEvent.clientX - startX
                              const deltaPercent = (deltaX / trackWidth) * 100
                              const deltaValue = (deltaPercent / 100) * 15000
                              const newValue = Math.max(0, Math.min(15000, startValue + deltaValue))
                              
                              if (newValue >= filters.avg_price_min) {
                                tempValue = Math.round(newValue / 100) * 100
                                // Update visual position immediately without API call
                                e.currentTarget.style.left = `${(tempValue / 15000) * 100}%`
                                // Update the active range visual
                                const activeRange = e.currentTarget.parentElement?.querySelector('.active-range') as HTMLElement
                                if (activeRange) {
                                  activeRange.style.width = `${((tempValue - filters.avg_price_min) / 15000) * 100}%`
                                }
                              }
                            }
                            
                            const handleMouseUp = () => {
                              // Only call API when mouse is released
                              if (tempValue !== filters.avg_price_max) {
                                handleFilterChange('avg_price_max', tempValue)
                              }
                              document.removeEventListener('mousemove', handleMouseMove)
                              document.removeEventListener('mouseup', handleMouseUp)
                            }
                            
                            document.addEventListener('mousemove', handleMouseMove)
                            document.addEventListener('mouseup', handleMouseUp)
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    {/* Quick Preset Buttons */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            avg_price_min: 0,
                            avg_price_max: 3000
                          }))
                        }}
                        className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                      >
                        Under €3k
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            avg_price_min: 3000,
                            avg_price_max: 6000
                          }))
                        }}
                        className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                      >
                        €3k-€6k
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            avg_price_min: 6000,
                            avg_price_max: 9000
                          }))
                        }}
                        className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                      >
                        €6k-€9k
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            avg_price_min: 9000,
                            avg_price_max: 15000
                          }))
                        }}
                        className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                      >
                        €9k+
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            avg_price_min: 0,
                            avg_price_max: 15000
                          }))
                        }}
                        className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100"
                      >
                        All Prices
                      </button>
                    </div>
                  </div>
                </div>



            {/* Launch Date Range */}
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Launch Date Range</label>
              
              {/* Quick Date Range Buttons */}
              <div className="flex flex-wrap gap-2 mb-3">
              <button
                  type="button"
                  onClick={() => {
                    const thirtyDaysAgo = new Date()
                    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
                    setFilters(prev => ({
                      ...prev,
                      launch_date_from: thirtyDaysAgo.toISOString().split('T')[0],
                      launch_date_to: new Date().toISOString().split('T')[0]
                    }))
                  }}
                  className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                >
                  Last 30 Days
              </button>
              <button
                  type="button"
                  onClick={() => {
                    const threeMonthsAgo = new Date()
                    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
                    setFilters(prev => ({
                      ...prev,
                      launch_date_from: threeMonthsAgo.toISOString().split('T')[0],
                      launch_date_to: new Date().toISOString().split('T')[0]
                    }))
                  }}
                  className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                >
                  Last 3 Months
              </button>
                <button
                  type="button"
                  onClick={() => {
                    const thisYear = new Date()
                    thisYear.setMonth(0, 1) // January 1st
                    setFilters(prev => ({
                      ...prev,
                      launch_date_from: thisYear.toISOString().split('T')[0],
                      launch_date_to: new Date().toISOString().split('T')[0]
                    }))
                  }}
                  className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                >
                  This Year
                </button>
                    <button
                  type="button"
                  onClick={() => {
                    setFilters(prev => ({
                      ...prev,
                      launch_date_from: '',
                      launch_date_to: ''
                    }))
                  }}
                  className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100"
                >
                  Clear Dates
                    </button>
      </div>

              {/* Date Input Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                  <input
                    type="date"
                    value={filters.launch_date_from}
                    onChange={(e) => handleFilterChange('launch_date_from', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
              </div>
              <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                  <input
                    type="date"
                    value={filters.launch_date_to}
                    onChange={(e) => handleFilterChange('launch_date_to', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
              </div>
            </div>
            </div>

            {/* Clear Filters */}
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                Clear All
            </button>
          </div>
                </div>
                  </div>
                )}

      {/* Full-Screen Map Container */}
      <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-0 m-4 sm:m-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading projects map...</p>
                  </div>
                  </div>
        ) : projectsWithCoordinates.length === 0 ? (
          <div className="flex items-center justify-center h-full p-4">
            <div className="text-center">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Projects Found</h3>
              <p className="text-gray-500 mb-4 text-sm sm:text-base">
                {projects.length === 0 
                  ? 'No projects match your current filters.'
                  : 'No projects have location coordinates available for mapping.'
                }
              </p>
              {projects.length === 0 && (
              <button
                  onClick={clearFilters} 
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                  Clear Filters
              </button>
              )}
            </div>
          </div>
        ) : (
          <GoogleMapsViewer
            projects={projectsWithCoordinates}
            onProjectSelect={handleProjectSelect}
            height="100%"
            className="rounded-lg h-full"
          />
        )}
      </div>

      {/* Project Detail Modal */}
        <ProjectDetailModal
          project={selectedProject}
          isOpen={!!selectedProject}
          onClose={() => setSelectedProject(null)}
          navigate={navigate}
        />
    </div>
  )
}